import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import {
  CONTROLLED_ROW_TYPES,
  ENROUTE_ROW_TYPES,
  airspaceOnRoute,
  buildIcaoFlightPlan,
  formatHhMm,
  latToDms,
  lonToDms,
  routeFrequencyRows,
} from '@jupi/shared'
import { RemoteContent, Title, formatDateTime, useAuth, useClubToday, useSnackbar } from '@jupi/ui'
import { useAirspaceShapes } from '../../api/plan'
import { PilotLogSheet } from './PilotLogSheet'
import { pad3 } from './PlanPage'
import { usePlanDraft } from './usePlanDraft'

// The operational flight plan: everything needed in the cockpit. On screen
// it reads as cards; Print gives the pilot log sheet (#176), an A4
// landscape page folded into two A5. It is drawn from the cached plan, so
// it stays readable at the airfield with no signal.
export default function OfpPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const wide = useMediaQuery(theme.breakpoints.up('md'))
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const editor = usePlanDraft()
  const { draft, figures, aircraft } = editor
  const [persons, setPersons] = useState('1')
  const today = useClubToday()

  const legs = draft?.legs ?? []
  const routable = legs.length >= 2
  const shapes = useAirspaceShapes(routable)
  const routeAirspace = useMemo(() => {
    if (!shapes.data) return null
    const points = legs.filter((l) => l.lat !== null && l.lon !== null).map((l) => ({ lat: l.lat!, lon: l.lon! }))
    const rows = routeFrequencyRows(airspaceOnRoute(points, shapes.data, [...CONTROLLED_ROW_TYPES, ...ENROUTE_ROW_TYPES]))
    return {
      controlled: rows.filter((r) => CONTROLLED_ROW_TYPES.includes(r.type)),
      enRoute: rows.filter((r) => ENROUTE_ROW_TYPES.includes(r.type) && r.freq),
    }
  }, [shapes.data, legs])
  const fpl =
    draft && figures && legs.length >= 2
      ? buildIcaoFlightPlan({
          registration: draft.registration ?? '',
          aircraftType: aircraft?.aircraftType ?? null,
          equipment: aircraft?.equipment ?? null,
          transponderMode: aircraft?.transponderMode ?? null,
          pbn: aircraft?.pbn ?? null,
          departure: legs[0]!.code,
          destination: legs[legs.length - 1]!.code,
          via: legs.slice(1, -1).map((leg) => leg.code),
          cruiseKt: figures.cruiseKt,
          eetMinutes: figures.totals.minutes,
          enduranceMinutes: figures.fuel?.enduranceMinutes ?? 0,
          persons: Number(persons) || 1,
          emergencyRadio: aircraft?.emergencyRadio ?? null,
          colors: aircraft?.colors ?? null,
          pilotName: user?.displayName ?? '',
        })
      : null

  const copy = async () => {
    if (!fpl) return
    const text = fpl.map((f) => `${f.number} ${t(`plan.ofp.fields.${f.item}`)}: ${f.value}`).join('\n')
    try {
      await navigator.clipboard.writeText(text)
      showSnackbar(t('plan.ofp.copied'))
    } catch {
      showSnackbar(t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title label={t('plan.ofp.title')} />
        <Button data-no-print startIcon={<Icon icon="mdi:printer-outline" />} onClick={() => window.print()} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          {t('plan.ofp.print')}
        </Button>
      </Stack>
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {draft && figures ? (
          legs.length < 2 ? (
            <Alert severity="info">{t('plan.ofp.empty')}</Alert>
          ) : (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
                      {legs[0]!.code} → {legs[legs.length - 1]!.code}
                    </Typography>
                    <Typography color="text.secondary">
                      {draft.registration ?? ''}
                      {aircraft?.aircraftType ? ` · ${aircraft.aircraftType}` : ''}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    <Fact label={t('plan.editor.dist')} value={`${figures.totals.distanceNm.toFixed(1)} NM`} />
                    <Fact label={t('plan.editor.ete')} value={formatHhMm(figures.totals.minutes)} />
                    {figures.cruiseKt !== null ? <Fact label="TAS" value={`${Math.round(figures.cruiseKt)} kt`} /> : null}
                    {figures.weightBalance ? (
                      <Chip color={figures.weightBalance.ok ? 'success' : 'error'} label={figures.weightBalance.ok ? t('plan.editor.wbOk') : t('plan.editor.wbNotOk')} />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {user?.displayName}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {t('plan.ofp.navLog')}
                  </Typography>
                  {wide ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['#', t('plan.waypoints.code'), t('plan.editor.wind'), 'TAS', t('plan.editor.tc'), t('plan.editor.mh'), t('plan.editor.dist'), t('plan.editor.gs'), t('plan.editor.ete'), 'COM', 'ATO'].map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {legs.map((leg, index) => {
                          const f = figures.legs[index]!
                          return (
                            <TableRow key={index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{leg.code}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{index > 0 ? `${pad3(leg.windDir)}/${Math.round(leg.windSpeedKt)}` : ''}</TableCell>
                              <TableCell>{index > 0 && f.tasKt !== null ? Math.round(f.tasKt) : ''}</TableCell>
                              <TableCell>{f.trueCourse !== null ? pad3(f.trueCourse) : ''}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{f.magHeading !== null ? pad3(f.magHeading) : ''}</TableCell>
                              <TableCell>{f.distanceNm?.toFixed(1) ?? ''}</TableCell>
                              <TableCell>{f.groundSpeedKt !== null ? Math.round(f.groundSpeedKt) : ''}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{f.minutes !== null ? formatHhMm(f.minutes) : ''}</TableCell>
                              <TableCell>{leg.com}</TableCell>
                              {/* Actual time over, written in flight. */}
                              <TableCell sx={{ minWidth: 60, borderBottom: '1px solid' }} />
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Stack spacing={1}>
                      {legs.map((leg, index) => {
                        const f = figures.legs[index]!
                        return (
                          <Box key={index} sx={{ borderTop: index ? 1 : 0, borderColor: 'divider', pt: index ? 1 : 0 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem' }}>{leg.code}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                                {leg.name}
                              </Typography>
                              {leg.com ? <Typography variant="body2">COM {leg.com}</Typography> : null}
                            </Stack>
                            {index > 0 && f.status === 'ok' ? (
                              <Typography sx={{ fontFamily: 'monospace' }}>
                                MH {pad3(f.magHeading)} · GS {Math.round(f.groundSpeedKt ?? 0)} · {formatHhMm(f.minutes ?? 0)} · {f.distanceNm?.toFixed(1)} NM
                              </Typography>
                            ) : null}
                            {leg.lat !== null && leg.lon !== null ? (
                              <Typography variant="caption" color="text.secondary">
                                {latToDms(leg.lat)} {lonToDms(leg.lon)}
                              </Typography>
                            ) : null}
                          </Box>
                        )
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              {figures.fuel ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('plan.weight.fuelPlan')}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Fact label={t('plan.editor.fuelTrip')} value={`${figures.fuel.tripUsg.toFixed(1)} USG`} />
                      <Fact label={t('plan.weight.required')} value={`${figures.fuel.requiredUsg.toFixed(1)} USG`} />
                      <Fact label={t('plan.weight.usable')} value={`${figures.fuel.usableUsg.toFixed(1)} USG`} />
                      <Fact label={t('plan.weight.onArrival')} value={`${figures.fuel.onArrivalUsg.toFixed(1)} USG`} />
                      <Fact label={t('plan.weight.endurance')} value={formatHhMm(figures.fuel.enduranceMinutes)} />
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              {fpl ? (
                <Card>
                  <CardContent>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">{t('plan.ofp.fpl')}</Typography>
                      <Button data-no-print startIcon={<Icon icon="mdi:content-copy" />} onClick={() => void copy()}>
                        {t('plan.ofp.copy')}
                      </Button>
                    </Stack>
                    <TextField
                      data-no-print
                      size="small"
                      label={t('plan.ofp.persons')}
                      value={persons}
                      onChange={(e) => setPersons(e.target.value.replace(/\D/g, '').slice(0, 2))}
                      slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                      sx={{ width: 140, mb: 1 }}
                    />
                    <Table size="small">
                      <TableBody>
                        {fpl.map((f) => (
                          <TableRow key={f.item}>
                            <TableCell sx={{ pl: 0, color: 'text.secondary', width: '45%' }}>
                              {f.number} · {t(`plan.ofp.fields.${f.item}`)}
                            </TableCell>
                            <TableCell sx={{ pr: 0, fontFamily: 'monospace', fontWeight: 600, overflowWrap: 'anywhere' }}>{f.value || '–'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : null}

              {/* Which copy this is: at the airfield it may be the one
                  cached on the phone rather than a fresh one. */}
              {editor.updatedAt ? (
                <Typography variant="caption" color="text.secondary">
                  {t('plan.ofp.savedAt', { time: formatDateTime(editor.updatedAt) })}
                </Typography>
              ) : null}
            </Stack>
          )
        ) : null}
      </RemoteContent>
      {/* What Print prints: beside the app, which is hidden for it. */}
      {draft && figures && routable
        ? createPortal(
            <div className="pilot-log-print">
              <PilotLogSheet
                legs={legs}
                figures={figures}
                aircraft={aircraft}
                registration={draft.registration}
                windDir={draft.windDir}
                windSpeedKt={draft.windSpeedKt}
                reserveMinutes={editor.reserveMinutes}
                pilotName={user?.displayName ?? ''}
                persons={Number(persons) || 1}
                today={today}
                savedAt={editor.updatedAt}
                airspace={routeAirspace?.controlled ?? null}
                enRoute={routeAirspace?.enRoute ?? []}
              />
            </div>,
            document.body,
          )
        : null}
    </Box>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{value}</Typography>
    </Box>
  )
}
